<template>
  <div
    :class="[
      {
        'horizontally-positioned': horizontallyPositioned,
        'vertically-positioned': verticallyPositioned,
        'left-aligned': leftAligned,
        'right-aligned': rightAligned,
      },
    ]"
  >
    <div v-if="verticallyPositioned" ref="sentinel" class="sentinel"></div>
    <TransitionFadeInOut>
      <div
        v-if="indicatorIsVisible"
        :class="[
          'scrollable-container-indicator',
          {
            'vertical-indicator': verticallyPositioned,
            'horizontal-indicator': horizontallyPositioned,
            'has-button': showButton,
            'left-aligned': leftAligned,
            'right-aligned': rightAligned,
          },
        ]"
        :style="indicatorStyle"
      >
        <FriedRoundedButton
          v-if="showButton"
          v-tooltip="{
            content: buttonTooltip,
            delay: { show: 300, hide: 100 },
            placement: Placement.Top,
          }"
          data-external="scrollable-container-indicator-button"
          @click="$emit('click')"
        >
          <template #icon>
            <FriedIcon v-if="leftAligned" icon="chevron-left" />
            <FriedIcon v-else-if="rightAligned" icon="chevron-right" />
          </template>
        </FriedRoundedButton>
      </div>
    </TransitionFadeInOut>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, ref, computed } from 'vue';
import type { PropType } from 'vue';
import { TransitionFadeInOut, Placement } from '@getaccept/fried-tofu';
import useBreakpoints from '../composables/breakpoints.composable';

export default defineComponent({
  components: {
    TransitionFadeInOut,
  },
  props: {
    scrollableContainer: {
      type: HTMLElement as PropType<HTMLElement>,
      required: true,
      default: null,
    },
    verticallyPositioned: {
      type: Boolean,
    },
    leftAligned: {
      type: Boolean,
    },
    horizontallyPositioned: {
      type: Boolean,
    },
    rightAligned: {
      type: Boolean,
    },
    showButton: {
      type: Boolean,
    },
    buttonTooltip: {
      type: String,
      default: null,
    },
  },
  emits: ['click'],
  setup(props) {
    const indicatorIsVisible = ref(false);
    const scrollableContainerWidth = ref(0);
    const sentinel = ref(null);

    const observer = new IntersectionObserver(
      ([
        {
          boundingClientRect: { bottom },
          isIntersecting,
        },
      ]) => {
        const isBottomVisible = bottom && bottom < window.innerHeight;
        indicatorIsVisible.value = isIntersecting || isBottomVisible ? false : true;
      },
      {
        threshold: 1,
      }
    );

    const { isXXSmallScreen } = useBreakpoints();

    const waitForScrollableContainerToMount = async () => {
      if (!props.scrollableContainer.offsetWidth) {
        setTimeout(waitForScrollableContainerToMount, 100);
        return;
      }

      if (props.verticallyPositioned) {
        startObserver();
        return;
      }

      scrollableContainerWidth.value = isXXSmallScreen ? 100 : 200;
      props.scrollableContainer.addEventListener('scroll', checkScrollEnd);
      checkScrollEnd();
    };

    const INDICATOR_HEIGHT = 208;

    const checkScrollEnd = () => {
      const { scrollLeft, clientWidth, scrollWidth } = props.scrollableContainer;
      if (props.leftAligned) {
        indicatorIsVisible.value = scrollLeft > 0;
        return;
      }

      const isScrollAtEnd =
        scrollLeft + clientWidth >= scrollWidth - (isXXSmallScreen ? 10 : INDICATOR_HEIGHT);
      indicatorIsVisible.value = !isScrollAtEnd;
    };

    const startObserver = () => {
      scrollableContainerWidth.value = props.scrollableContainer.offsetWidth;
      observer.observe(sentinel.value);
    };

    const indicatorStyle = computed(() => {
      const position = props.leftAligned ? 'left' : 'right';
      const bottom = props.verticallyPositioned ? '0px' : null;
      const height = props.verticallyPositioned
        ? `${INDICATOR_HEIGHT}px`
        : `${props.scrollableContainer.clientHeight}px`;
      const backgroundDirection = props.verticallyPositioned
        ? '180deg'
        : props.leftAligned
        ? '270deg'
        : '90deg';

      return {
        width: `${scrollableContainerWidth.value}px`,
        bottom,
        height,
        background: `linear-gradient(${backgroundDirection}, rgb(255 255 255 / 0%) 0%, rgb(255 255 255 / 99%) 100%)`,
        [position]: `${
          props.verticallyPositioned
            ? '0'
            : `${(window.innerWidth - props.scrollableContainer.offsetWidth) / 2}px`
        }`,
      };
    });

    onMounted(() => {
      waitForScrollableContainerToMount();
    });

    onBeforeUnmount(() => {
      if (props.verticallyPositioned) {
        observer.disconnect();
        return;
      }
      props.scrollableContainer.removeEventListener('scroll', checkScrollEnd);
    });

    return {
      indicatorIsVisible,
      scrollableContainerWidth,
      sentinel,
      indicatorStyle,
      Placement,
    };
  },
});
</script>

<style lang="scss" scoped>
.vertically-positioned {
  position: relative;
  height: 100%;
  z-index: 2;
}

.horizontally-positioned {
  position: absolute;
  height: 100%;
  z-index: 2;

  &.left-aligned {
    left: var(--spacing-100);
  }

  &.right-aligned {
    right: var(--spacing-100);
  }
}

.sentinel {
  height: 1px;
}

.scrollable-container-indicator {
  z-index: $z1;
  pointer-events: none;

  &.vertical-indicator {
    position: fixed;
  }

  &.horizontal-indicator {
    position: sticky;
  }

  &.has-button {
    pointer-events: unset;
    display: flex;
    align-items: center;

    &.left-aligned {
      justify-content: flex-start;
    }

    &.right-aligned {
      justify-content: flex-end;
    }
  }
}
</style>
